import {isEmpty, take, filter, isNumber, isEqual, last} from 'lodash';
import {useState, useRef, useEffect, useCallback, useMemo} from 'react';
import {SocketLikeWithNamespace} from 'socket.io-react-hook/lib/cjs/types';

import {AudioStreamErrorCode, AudioStreamState, SocketEvent, SttClientState} from 'enums';
import {logger} from 'utils';

import {usePrevious} from './previous-value';
import {useSocketEventCallback} from './socket-event-callback';

const FULL_RESTART_ON_FAILED_COUNT = 5;
const LOG_PREFIX = '[Stream Record]';
const withLogPrefix = (message: string) => `${LOG_PREFIX} ${message}`;
// Safari has no MediaRecorder, so it captures raw PCM instead.
const DEFAULT_PCM_SAMPLE_RATE = 16000;
const isSafariBrowser = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

type Props = {
    socket: SocketLikeWithNamespace;
    shouldRecord: boolean;
    sessionId: string;
    participantId: string;
    activityId: string;
    tracks: MediaStreamTrack[];
    transcriptionLanguage: string;
    debug?: boolean;
    onError?: (error: {code: AudioStreamErrorCode}) => void;
    // The server stopped the stream on its own — the caller owns the recording state and has to clear it.
    onStopped?: () => void;
    onStateChange?: (state: AudioStreamState | SttClientState) => void;
}

// A browser may ignore the requested AudioContext rate (Safari hands back its own 48 kHz), and the receiving
// side decodes at the rate we declare — so bring the samples to the requested one instead of declaring 48.
// Averaging the samples that collapse into one, rather than picking every Nth, keeps the high end from folding
// back as a metallic sizzle; it also handles a non-integer ratio, where plain decimation has nothing to drop.
function resamplePCM(input: Float32Array, fromRate: number, toRate: number): Float32Array {
    if (fromRate === toRate || fromRate < toRate) {
        return input;
    }
    const ratio = fromRate / toRate;
    const output = new Float32Array(Math.floor(input.length / ratio));

    for (let i = 0; i < output.length; i++) {
        const start = Math.floor(i * ratio);
        const end = Math.min(Math.floor((i + 1) * ratio), input.length);
        let sum = 0;
        for (let j = start; j < end; j++) {
            sum += input[j];
        }
        output[i] = sum / Math.max(1, end - start);
    }

    return output;
}

// PCM conversion utility for Safari - converts Float32 audio samples to 16-bit PCM
function floatTo16BitPCM(float32Array: Float32Array): ArrayBuffer {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    let offset = 0;

    for (let i = 0; i < float32Array.length; i++, offset += 2) {
        // ограничиваем значение от -1.0 до 1.0
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        // переводим float → int16
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }

    return buffer;
}

export function useAudioStreamRecord({
    socket, shouldRecord, sessionId, participantId, activityId, tracks, transcriptionLanguage, debug,
    onError, onStopped, onStateChange
}: Props) {
    // Actual context rate: a browser may ignore the requested one, and the server decodes by what we declare.
    const pcmRateRef = useRef(DEFAULT_PCM_SAMPLE_RATE);
    const usePcm = useMemo(() => isSafariBrowser(), []);
    const bufferRef = useRef<{event: BlobEvent, index: number}[]>([]);
    const pcmBufferRef = useRef<{buffer: ArrayBuffer, index: number}[]>([]);
    const audioStreamStateRef = useRef<AudioStreamState>(null);
    const mediaRecorderRef = useRef<MediaRecorder>(null);
    const audioContextRef = useRef<AudioContext>(null);
    const processorRef = useRef<ScriptProcessorNode>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode>(null);
    const pcmActiveRef = useRef(false);
    const prevActivityId = usePrevious(activityId);
    const [fullRestartOnFailedCount, setFullRestartOnFailedCount] = useState(0);

    const audioTrack = useMemo(() => filter(tracks, (track) => track.kind === 'audio')[0], [tracks]);
    const [prevEnabled, setPrevEnabled] = useState(false);

    const prevShouldRecord = usePrevious(shouldRecord);
    const prevTracks = usePrevious(tracks);
    const prevTranscriptionLanguage = usePrevious(transcriptionLanguage);

    const setAudioStreamState = useCallback((value: AudioStreamState) => {
        logger.info(withLogPrefix('SetAudioStreamState'), value);
        audioStreamStateRef.current = value;
        onStateChange?.(value);
    }, [onStateChange]);

    const sendChunks = useCallback(async (chunks: {event: BlobEvent, index: number}[]) => {
        try {
            if (debug) {
                logger.debug(withLogPrefix('SendChunks'), chunks.map((e) => e.index));
            }
            for (const chunk of chunks) {
                // Safari compatibility: use FileReader as fallback if arrayBuffer is not available
                let buffer: ArrayBuffer;

                if (chunk.event.data.arrayBuffer) {
                    buffer = await chunk.event.data.arrayBuffer();
                } else {
                    // Fallback for older browsers/Safari
                    buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result as ArrayBuffer);
                        reader.onerror = reject;
                        reader.readAsArrayBuffer(chunk.event.data);
                    });
                }

                socket.emit(
                    SocketEvent.ActiveSessionActivityStreamChunk,
                    {
                        activityId,
                        sessionId,
                        participantId,
                        index: chunk.index,
                        audioBuffer: buffer
                    }
                );
            }
        } catch (error) {
            logger.error(withLogPrefix('Failed to send chunk'), error);
            onError?.({code: AudioStreamErrorCode.SendChunks});
        }
    }, [activityId, participantId, sessionId, socket, debug, onError]);

    const sendPCMChunks = useCallback((chunks: {buffer: ArrayBuffer, index: number}[]) => {
        try {
            if (debug) {
                logger.debug(withLogPrefix('SendPCMChunks'), chunks.map((e) => e.index));
            }
            for (const chunk of chunks) {
                socket.emit(
                    SocketEvent.ActiveSessionActivityStreamChunk,
                    {
                        activityId,
                        sessionId,
                        participantId,
                        index: chunk.index,
                        audioBuffer: chunk.buffer
                    }
                );
            }
        } catch (error) {
            logger.error(withLogPrefix('Failed to send PCM chunk'), error);
            onError?.({code: AudioStreamErrorCode.SendChunks});
        }
    }, [activityId, participantId, sessionId, socket, debug, onError]);

    const sendFirstChunks = useCallback(() => {
        if (usePcm && !isEmpty(pcmBufferRef.current)) {
            if (pcmBufferRef.current[0].index === 0) {
                if (debug) {
                    logger.debug(withLogPrefix('SendFirstPCMChunks'), {bufferSize: pcmBufferRef.current.length});
                }
                sendPCMChunks(take(pcmBufferRef.current, 1));
            } else {
                sendPCMChunks(take(pcmBufferRef.current, 5));
            }
        } else if (!isEmpty(bufferRef.current)) {
            if (bufferRef.current[0].index === 0) {
                if (debug) {
                    logger.debug(withLogPrefix('SendFirstChunks'), {bufferSize: bufferRef.current.length});
                }
                sendChunks(take(bufferRef.current, 1));
            } else {
                sendChunks(take(bufferRef.current, 5));
            }
        }
    }, [debug, usePcm, sendChunks, sendPCMChunks]);

    const onRecordData = useCallback((event: BlobEvent, index: number) => {
        if (debug) {
            logger.debug(withLogPrefix(
                `RecordData. index=${index}, state=${audioStreamStateRef.current}, bufferSize=${bufferRef.current.length}`
            ));
        }
        if (audioStreamStateRef.current === 'closed' ||
            last(bufferRef.current)?.index < index - 1 ||
            index > 0 && isEmpty(bufferRef.current) && !audioStreamStateRef.current
        ) {
            return;
        }
        bufferRef.current = [...bufferRef.current, {event, index}];
        if (socket.connected) {
            if (!audioStreamStateRef.current) {
                setAudioStreamState(AudioStreamState.Request);
                socket.emit(SocketEvent.ActiveSessionActivityRequestStreamStart, {
                    sessionId,
                    participantId,
                    activityId,
                    mimeType: mediaRecorderRef.current?.mimeType || 'audio/webm'
                });
            } else if (audioStreamStateRef.current === AudioStreamState.Active) {
                sendFirstChunks();
            } else if (audioStreamStateRef.current === AudioStreamState.Request && bufferRef.current.length >= 60) {
                if (debug) {
                    logger.debug(withLogPrefix('ActiveSessionActivityRequestStreamState'), {bufferSize: bufferRef.current.length});
                }
                socket.emit(SocketEvent.ActiveSessionActivityRequestStreamState, {sessionId, participantId, activityId});
            }
        }
    }, [activityId, participantId, sendFirstChunks, sessionId, setAudioStreamState, socket, debug]);

    const onPCMData = useCallback((buffer: ArrayBuffer, index: number) => {
        if (debug) {
            logger.debug(withLogPrefix(
                `PCMData. index=${index}, state=${audioStreamStateRef.current}, bufferSize=${pcmBufferRef.current.length}`
            ));
        }
        if (audioStreamStateRef.current === 'closed' ||
            last(pcmBufferRef.current)?.index < index - 1 ||
            index > 0 && isEmpty(pcmBufferRef.current) && !audioStreamStateRef.current
        ) {
            return;
        }
        pcmBufferRef.current = [...pcmBufferRef.current, {buffer, index}];
        if (socket.connected) {
            if (!audioStreamStateRef.current) {
                setAudioStreamState(AudioStreamState.Request);
                socket.emit(SocketEvent.ActiveSessionActivityRequestStreamStart, {
                    sessionId,
                    participantId,
                    activityId,
                    mimeType: `audio/l16;rate=${pcmRateRef.current};channels=1`
                });
            } else if (audioStreamStateRef.current === AudioStreamState.Active) {
                sendFirstChunks();
            } else if (audioStreamStateRef.current === AudioStreamState.Request && pcmBufferRef.current.length >= 60) {
                if (debug) {
                    logger.debug(withLogPrefix('ActiveSessionActivityRequestStreamState'), {bufferSize: pcmBufferRef.current.length});
                }
                socket.emit(SocketEvent.ActiveSessionActivityRequestStreamState, {sessionId, participantId, activityId});
            }
        }
    }, [activityId, participantId, sendFirstChunks, sessionId, setAudioStreamState, socket, debug]);

    // Ends the spoken turn: flush whatever is still buffered, THEN tell the server. Chunks are normally sent one
    // at a time as they arrive, so at this point the tail of the phrase can still be sitting in the buffer.
    // Already-acknowledged chunks are dropped from it, and re-sent ones are ignored server-side by index.
    const endAudioStream = useCallback(() => {
        if (!isEmpty(bufferRef.current)) {
            sendChunks(bufferRef.current);
        }
        if (!isEmpty(pcmBufferRef.current)) {
            sendPCMChunks(pcmBufferRef.current);
        }
        socket.emit(SocketEvent.ActiveSessionActivityRequestStreamStop, {sessionId, participantId, activityId});
    }, [activityId, participantId, sendChunks, sendPCMChunks, sessionId, socket]);

    const stopAudioRecording = useCallback(() => {
        const recorder = mediaRecorderRef.current;
        const wasPcmRecording = pcmActiveRef.current;

        if (recorder) {
            // MediaRecorder emits its final chunk asynchronously, AFTER stop() — and on a short phrase that
            // chunk holds most of the speech. Ending the stream before it arrives is what cut phrases off.
            recorder.onstop = () => endAudioStream();
            recorder.stop();
            mediaRecorderRef.current = null;
        }

        // Stop AudioContext and processor if exists (Safari PCM)
        pcmActiveRef.current = false;
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (sourceRef.current) {
            sourceRef.current.disconnect();
            sourceRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }

        // Safari has no MediaRecorder (raw PCM path), so there is no onstop to wait for.
        if (!recorder && wasPcmRecording) {
            endAudioStream();
        }
    }, [endAudioStream]);

    const startAudioRecording = useCallback(async () => {
        if (debug) {
            logger.debug(withLogPrefix('StartAudioRecording'));
        }

        if (!audioTrack) {
            onError?.({code: AudioStreamErrorCode.Connection});
            return;
        }

        if (audioTrack.readyState !== 'live') {
            logger.warn(withLogPrefix('StartAudioRecording failed — track not live'));
            onError?.({code: AudioStreamErrorCode.Connection});
            return;
        }

        stopAudioRecording();

        const stream = new MediaStream([audioTrack]);

        // Raw PCM capture: the only option in Safari (no MediaRecorder), and the required format for the
        // voice-agent stream, which takes PCM16LE from every browser.
        if (usePcm) {
            try {
                logger.info(withLogPrefix('Using AudioContext for PCM capture'));

                pcmActiveRef.current = false;
                if (processorRef.current) {
                    processorRef.current.disconnect();
                }
                if (sourceRef.current) {
                    sourceRef.current.disconnect();
                }
                if (audioContextRef.current) {
                    try {
                        await audioContextRef.current.close();
                    // eslint-disable-next-line no-empty
                    } catch {}
                }

                pcmActiveRef.current = true;

                const requestedRate = DEFAULT_PCM_SAMPLE_RATE;
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
                    sampleRate: requestedRate
                });
                // Requested rate honoured → nothing to do; ignored (Safari) → resample down to it, so what we
                // declare is always what we send. Upsampling is not attempted: declare the real rate instead.
                const captureRate = audioContext.sampleRate;
                pcmRateRef.current = Math.min(captureRate, requestedRate);
                const source = audioContext.createMediaStreamSource(stream);
                const processor = audioContext.createScriptProcessor(4096, 1, 1);

                audioContextRef.current = audioContext;
                sourceRef.current = source;
                processorRef.current = processor;

                let index = 0;
                let pcmBuffer: Float32Array[] = [];
                let lastSendTime = Date.now();

                processor.onaudioprocess = (e) => {
                    if (!pcmActiveRef.current) {
                        return;
                    }
                    const inputBuffer = e.inputBuffer.getChannelData(0);
                    pcmBuffer.push(new Float32Array(inputBuffer));

                    const now = Date.now();
                    if (now - lastSendTime >= 1000) {
                        const totalLength = pcmBuffer.reduce((sum, buf) => sum + buf.length, 0);
                        const combinedBuffer = new Float32Array(totalLength);
                        let offset = 0;
                        for (const buf of pcmBuffer) {
                            combinedBuffer.set(buf, offset);
                            offset += buf.length;
                        }

                        const pcm16 = floatTo16BitPCM(resamplePCM(combinedBuffer, captureRate, pcmRateRef.current));
                        onPCMData(pcm16, index++);

                        pcmBuffer = [];
                        lastSendTime = now;
                    }
                };

                source.connect(processor);
                processor.connect(audioContext.destination);

                logger.info(withLogPrefix('Safari AudioContext started for PCM capture'));
            } catch (error) {
                logger.error(withLogPrefix('Failed to start Safari AudioContext'), error);
                onError?.({code: AudioStreamErrorCode.Connection});
            }
            return;
        }

        // Chrome/Firefox/Edge: Use MediaRecorder with WebM/Opus
        if (!window.MediaRecorder) {
            logger.error(withLogPrefix('StartAudioRecording failed - MediaRecorder not supported'));
            onError?.({code: AudioStreamErrorCode.Connection});
            return;
        }

        // Browser and MIME type detection according to task specification
        let mimeType: string | undefined;

        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            mimeType = 'audio/webm;codecs=opus'; // ✅ Chrome, Edge, Firefox
        } else if (MediaRecorder.isTypeSupported('audio/webm')) {
            mimeType = 'audio/webm';
        } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
            mimeType = 'audio/mp4'; // ✅ Safari fallback (AAC) - should not reach here in Safari
        } else {
            mimeType = ''; // default
        }

        if (debug) {
            logger.debug(withLogPrefix('Selected mimeType: ' + mimeType || 'default'));
        }

        try {
            const options = mimeType ? {mimeType} : {};
            const _mediaRecorder = new MediaRecorder(stream, options);

            mediaRecorderRef.current = _mediaRecorder;

            let index = 0;
            _mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    onRecordData?.(event, index++);
                }
            };

            _mediaRecorder.onerror = (event: any) => {
                logger.error(withLogPrefix('MediaRecorder error: '), event);
                onError?.({code: AudioStreamErrorCode.Connection});
            };

            _mediaRecorder.start(1000);
            if (debug) {
                logger.debug(withLogPrefix('MediaRecorder started'), {
                    mimeType: _mediaRecorder.mimeType,
                    state: _mediaRecorder.state
                });
            }
        } catch (error) {
            logger.error(withLogPrefix('Failed to start MediaRecorder'), error);
            onError?.({code: AudioStreamErrorCode.Connection});
        }
    }, [audioTrack, debug, usePcm, onError, onRecordData, onPCMData, stopAudioRecording]);

    const fullRestart = useCallback(() => {
        setAudioStreamState(null);
        stopAudioRecording();
        bufferRef.current = [];
        pcmBufferRef.current = [];
        startAudioRecording();
    }, [setAudioStreamState, startAudioRecording, stopAudioRecording]);

    // Server-initiated stop. Handled apart from ActiveSessionActivityStreamState: a provider-side close means
    // "reconnect", this one means "the turn is over" — restarting here would reopen the microphone the server
    // just switched off.
    useSocketEventCallback(socket, SocketEvent.ActiveSessionActivityStreamStopped, useCallback(() => {
        logger.info(withLogPrefix('Stream stopped by the server'));
        setAudioStreamState(AudioStreamState.Closed);
        stopAudioRecording();
        onStopped?.();
    }, [setAudioStreamState, stopAudioRecording, onStopped]));

    useSocketEventCallback(socket, SocketEvent.ActiveSessionActivityStreamChunkHandled,
        useCallback(({handledIndex}: {handledIndex: number}) => {
            if (debug) {
                logger.debug(withLogPrefix('ActiveSessionActivityStreamChunkHandled'), {handledIndex});
            }
            if (isNumber(handledIndex)) {
                bufferRef.current = filter(bufferRef.current, ({index}) => index > handledIndex);
                pcmBufferRef.current = filter(pcmBufferRef.current, ({index}) => index > handledIndex);
            }
        }, [debug]));

    useSocketEventCallback(socket, SocketEvent.ActiveSessionActivityStreamState, useCallback(({state}: {state: SttClientState}) => {
        if (debug) {
            logger.debug(withLogPrefix('ActiveSessionActivityRequestStreamState'), state);
        }
        if (state === SttClientState.Active && audioStreamStateRef.current === AudioStreamState.Request) {
            setAudioStreamState(AudioStreamState.Active);
            sendFirstChunks();
        } else if ((state === SttClientState.Closed || state === SttClientState.Failed) && shouldRecord) {
            if (fullRestartOnFailedCount < FULL_RESTART_ON_FAILED_COUNT) {
                logger.warn(withLogPrefix(
                    `ActiveSessionActivityRequestStreamState = Failed. Restart count: ${fullRestartOnFailedCount + 1}`
                ));
                setFullRestartOnFailedCount((count) => count + 1);
                fullRestart();
            } else {
                onError?.({code: AudioStreamErrorCode.Connection});
                stopAudioRecording();
                setAudioStreamState(AudioStreamState.Closed);
            }
        }
    }, [fullRestart, fullRestartOnFailedCount, onError, sendFirstChunks, setAudioStreamState, shouldRecord, stopAudioRecording, debug]));

    useEffect(() => {
        if (!shouldRecord) {
            if (audioStreamStateRef.current) {
                setAudioStreamState(AudioStreamState.Closed);
            }
            if (!isEmpty(bufferRef.current)) {
                bufferRef.current = [];
            }
            if (!isEmpty(pcmBufferRef.current)) {
                pcmBufferRef.current = [];
            }
        }
        if (shouldRecord && audioStreamStateRef.current === AudioStreamState.Closed) {
            setAudioStreamState(null);
        }
    }, [setAudioStreamState, shouldRecord]);

    useEffect(() => {
        if (prevActivityId !== activityId) {
            stopAudioRecording();
        }
    }, [activityId, prevActivityId, stopAudioRecording]);

    useEffect(() => {
        if (shouldRecord && !prevShouldRecord && audioTrack) {
            startAudioRecording();
        } else if (!shouldRecord && prevShouldRecord) {
            setFullRestartOnFailedCount(0);
            stopAudioRecording();
        }
    }, [audioStreamStateRef, prevShouldRecord, shouldRecord, audioTrack, startAudioRecording, stopAudioRecording]);

    useEffect(() => {
        if (!prevTracks) {
            return;
        }
        const prevIds = prevTracks.map((t) => t.id);
        const currIds = tracks.map((t) => t.id);
        if (shouldRecord && !isEqual(prevIds, currIds)) {
            logger.info(withLogPrefix('Tracks array changed — full restart'));
            fullRestart();
        }
    }, [tracks, prevTracks, shouldRecord, fullRestart]);

    useEffect(() => {
        if (prevTranscriptionLanguage !== transcriptionLanguage && shouldRecord) {
            fullRestart();
        }
    }, [transcriptionLanguage, prevTranscriptionLanguage, shouldRecord, fullRestart]);

    useEffect(() => {
        if (shouldRecord && !prevEnabled && audioTrack?.enabled && audioStreamStateRef.current !== AudioStreamState.Active) {
            fullRestart();
        }
        setPrevEnabled(audioTrack?.enabled);
    }, [audioTrack?.enabled, fullRestart, prevEnabled, shouldRecord]);
}